import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import {
  Controller,
  FormProvider,
  useForm,
  useFormContext,
} from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import Box from 'ui-box';
import {
  Button,
  Checkbox,
  Dialog,
  FormField,
  Grid,
  HTMLSelect,
  IDialogProps,
  InputGroup,
  majorScale,
  useToast,
} from '../../../npaper';
import { useStores } from '../../../stores';
import {
  IIntegration,
  IIntegrationType,
} from '../../../stores/IntegrationStore';

export interface IIntegrationDetialsDialogProps
  extends Omit<Omit<IDialogProps, 'children'>, 'title'> {
  integrationType: IIntegrationType;
  integration?: IIntegration;
}

interface FormValues {}

export const IntegrationDetailsDialog: React.FC<IIntegrationDetialsDialogProps> =
  observer(({ integration, ...props }) => {
    const { integrationStore } = useStores();
    const toaster = useToast();
    const [isShown, setIsShown] = useState(props.isShown);
    const integrationType = integration
      ? integration.integration_type
      : props.integrationType;

    const missingAuth = !integration && integrationType.info.client_auth_url;

    const methods = useForm<FormValues>();
    const { handleSubmit, reset } = methods;

    const defaultConfiguration = integration ? integration.configuration : {};

    useEffect(() => {
      reset(defaultConfiguration);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [integration]);

    useEffect(() => {
      setIsShown(props.isShown);
    }, [props.isShown]);

    const onSubmit = async (values: FormValues) => {
      if (integration) {
        // Update
        try {
          await integrationStore.updateItem(integration, {
            configuration: values,
          });
        } catch (err) {
          toaster.showDefaultError(err);
          throw err;
        }
      } else {
        // create
        try {
          await integrationStore.createItem({
            key: integrationType.info.key,
            configuration: values,
          });
        } catch (err) {
          toaster.showDefaultError(err);
          throw err;
        }
      }
    };

    return (
      <Dialog
        {...props}
        isShown={isShown}
        closesOnEscape={!!integration}
        title={
          integration
            ? `Edit ${integrationType.info.name}`
            : `Add ${integrationType.info.name}`
        }
        actions={
          <>
            {integration && !integration.activated_at && (
              <Button
                intent="danger"
                primary
                marginRight="auto"
                onClick={() =>
                  integrationStore
                    .deleteItem(integration)
                    .then(() => setIsShown(false))
                    .catch(toaster.showDefaultError)
                }
              >
                <FormattedMessage defaultMessage="Delete integration" />
              </Button>
            )}
            {!integration && (
              <Button onClick={() => props.onClose()}>Cancel</Button>
            )}
            {!missingAuth && integration && (
              <Button
                onClick={(e: any) =>
                  handleSubmit(onSubmit)(e).then(() => setIsShown(false))
                }
              >
                Save changes
              </Button>
            )}
            {!missingAuth && integration && !integration.activated_at && (
              <Button
                primary
                onClick={(e: any) =>
                  handleSubmit(onSubmit)(e).then(() =>
                    integrationStore
                      .activate(integration)
                      .then(() => setIsShown(false))
                      .catch(toaster.showDefaultError)
                  )
                }
              >
                Activate integration
              </Button>
            )}
            {!missingAuth && integration && integration?.activated_at && (
              <Button
                primary
                intent="danger"
                onClick={() =>
                  integrationStore
                    .deactivate(integration)
                    .then(() => setIsShown(false))
                    .catch(toaster.showDefaultError)
                }
              >
                Deactive integration
              </Button>
            )}
          </>
        }
      >
        <FormProvider {...methods}>
          <form
            onSubmit={(e) =>
              handleSubmit(onSubmit)(e).then(() => setIsShown(false))
            }
          >
            <Grid
              padding={majorScale(2)}
              paddingTop={majorScale(1)}
              className="border-bottom"
            >
              {integrationType.schema.description && (
                <Box className="col-span-12">
                  <p className="muted mt-2">
                    {integrationType.schema.description}
                  </p>
                </Box>
              )}
            </Grid>
            <Box padding={majorScale(2)} className="grid">
              {missingAuth ? (
                <Button
                  primary
                  is="a"
                  target="blank"
                  rel="noopener noreferrer"
                  href={integrationType.info.client_auth_url}
                  className="col-span-4 col-start-4"
                >
                  Connect to {integrationType.info.name}
                </Button>
              ) : (
                integrationType.schema.properties &&
                Object.keys(integrationType.schema.properties).map((key, i) => (
                  <SchemaField
                    key={key}
                    schemaProperty={integrationType.schema.properties[key]}
                    name={key}
                  />
                ))
              )}
            </Box>
          </form>
        </FormProvider>
      </Dialog>
    );
  });

const SchemaField = ({
  schemaProperty,
  name,
}: {
  schemaProperty: {
    title?: string;
    description?: string;
    example?: string[];
    required?: boolean;
    type?: 'string' | 'boolean';
    'x-options'?: { label: string; value: string | number }[];
  };
  name: string;
}) => {
  const intl = useIntl();
  const { register, errors, control } = useFormContext();

  const rules = { required: schemaProperty.required };
  return (
    <FormField
      className="col-span-12 md:col-span-9"
      inline
      label={schemaProperty.title || ''}
      hint={schemaProperty.type !== 'boolean' ? schemaProperty.description : ''}
    >
      {schemaProperty.type === 'string' ? (
        schemaProperty['x-options'] ? (
          <Controller
            control={control}
            name={name}
            rules={rules}
            render={({ onChange, value }) => (
              <HTMLSelect
                placeholder="Choose a value"
                options={schemaProperty['x-options']}
                onChange={(v) => {
                  onChange(v);
                }}
                value={value}
              />
            )}
          />
        ) : (
          <InputGroup
            name={name}
            ref={register(rules)}
            placeholder={
              schemaProperty.example
                ? schemaProperty.example[0]
                : 'Write a value'
            }
            isInvalid={errors[name]}
            validationMessage={
              errors[name] &&
              (errors[name].type === 'required'
                ? intl.formatMessage({
                    defaultMessage: 'Please provide a value',
                  })
                : intl.formatMessage({
                    defaultMessage: 'Please enter a valid value',
                  }))
            }
          />
        )
      ) : schemaProperty.type === 'boolean' ? (
        <Controller
          control={control}
          name={name}
          rules={rules}
          render={({ onChange, value }) => (
            <Checkbox
              label={schemaProperty.description}
              checked={value}
              onChange={(f) => onChange(f)}
            />
          )}
        />
      ) : (
        'UNKOWN FIELD'
      )}
    </FormField>
  );
};
